/**
 * SalonHub課金・アカウント管理システム型定義
 * @description サロンがSalonHubサービスに支払う月額料金の管理
 */

// 無制限値の定数定義
export const UNLIMITED_VALUE = 999999;

// 無制限値を判定するユーティリティ関数
export const isUnlimited = (value: number): boolean => {
  return value >= UNLIMITED_VALUE;
};

// プラン情報（3プラン体系）
export interface PlanInfo {
  id: 'light' | 'standard' | 'premium';
  name: string;
  description: string;
  monthlyFee: number;           // 月額料金 (円)
  yearlyFee?: number;           // 年額料金 (円, 10%割引)
  billingCycle: 'monthly' | 'yearly';
  features: PlanFeature[];
  limits: PlanLimits;
  isActive: boolean;
  // Stripe統合用
  stripeProductId?: string;     // Stripe Product ID
  stripePriceId?: string;       // Stripe Price ID (月額)
  stripeYearlyPriceId?: string; // Stripe Price ID (年額)
}

// プラン機能
export interface PlanFeature {
  id: string;
  name: string;
  description: string;
  category: 'core' | 'advanced' | 'premium';
  included: boolean;
}

// プラン制限
export interface PlanLimits {
  staffCount: number;           // スタッフ登録上限（999999 = 無制限）
  reservationsPerMonth: number; // 月間予約件数上限（999999 = 無制限）
  customersLimit: number;       // 顧客登録上限（999999 = 無制限）
  storageGB: number;            // データストレージ上限（999999 = 無制限）
  aiAnalysisCount: number;      // AI分析月間上限（999999 = 無制限）
  supportLevel: 'email' | 'chat' | 'phone';
  // multiStore?: boolean;      // 削除: 各店舗が個別契約のため不要
  customBranding?: boolean;     // カスタムブランディング
  apiAccess?: boolean;          // API完全アクセス
}

// 請求レコード
export interface BillingRecord {
  id: string;
  invoiceNumber: string;        // 請求書番号
  billingDate: Date;            // 請求日
  dueDate: Date;                // 支払期限
  amount: number;               // 請求金額
  tax: number;                  // 消費税額
  totalAmount: number;          // 合計金額
  status: BillingStatus;
  paymentDate?: Date;           // 支払日
  paymentMethod?: PaymentMethodType;
  items: BillingItem[];
  downloadUrl?: string;         // 請求書PDF URL
}

// 請求ステータス
export type BillingStatus = 
  | 'pending'       // 支払待ち
  | 'paid'          // 支払完了
  | 'overdue'       // 期限超過
  | 'failed'        // 支払失敗
  | 'refunded';     // 返金済み

// 請求項目
export interface BillingItem {
  id: string;
  name: string;                 // "SalonHub Premium プラン"
  description?: string;         // "2025年9月分"
  quantity: number;             // 数量
  unitPrice: number;            // 単価
  totalPrice: number;           // 合計
  period: {
    start: Date;
    end: Date;
  };
}

// 支払い方法
export interface PaymentMethod {
  id: string;
  type: PaymentMethodType;
  isDefault: boolean;
  isActive: boolean;
  
  // クレジットカード情報
  card?: {
    brand: 'visa' | 'mastercard' | 'jcb' | 'amex';
    last4: string;              // 下4桁
    expMonth: number;           // 有効期限月
    expYear: number;            // 有効期限年
    holderName: string;         // カード名義人
  };
  
  // 銀行振込情報
  bankTransfer?: {
    bankName: string;
    accountNumber: string;
    accountHolder: string;
  };
}

export type PaymentMethodType = 'credit_card' | 'bank_transfer' | 'digital_wallet';

// SalonHub課金情報（統合）
export interface SalonHubBilling {
  // アカウント基本情報
  account: {
    storeId: string;
    storeName: string;
    contactEmail: string;
    billingEmail: string;
    taxId?: string;               // 法人番号等
    address?: BillingAddress;
  };
  
  // 現在のプラン
  currentPlan: PlanInfo;
  
  // 請求・支払い情報
  billing: {
    nextBillingDate: Date;
    billingHistory: BillingRecord[];
    paymentMethods: PaymentMethod[];
    defaultPaymentMethod?: string; // PaymentMethod.id
  };
  
  // 使用状況
  usage: {
    currentMonth: UsageStats;
    previousMonth: UsageStats;
    alerts: UsageAlert[];
  };
}

// 請求先住所
export interface BillingAddress {
  postalCode: string;
  prefecture: string;
  city: string;
  address1: string;
  address2?: string;
  building?: string;
}

// 使用状況統計
export interface UsageStats {
  period: {
    start: Date;
    end: Date;
  };
  staffCount: number;
  reservationCount: number;
  customerCount: number;
  storageUsedMB: number;
  aiAnalysisCount: number;
  
  // 制限に対する使用率
  usage: {
    staffUsage: number;           // 0-1 (使用率)
    reservationUsage: number;
    customerUsage: number;
    storageUsage: number;
    aiUsage: number;
  };
}

// 使用量アラート
export interface UsageAlert {
  id: string;
  type: 'warning' | 'limit_reached' | 'over_limit';
  category: 'staff' | 'reservations' | 'customers' | 'storage' | 'ai';
  message: string;
  threshold: number;            // 閾値
  currentUsage: number;         // 現在使用量
  limit: number;                // 上限値
  createdAt: Date;
  isRead: boolean;
}

// プラン変更リクエスト
export interface PlanChangeRequest {
  id: string;
  fromPlan: string;             // PlanInfo.id
  toPlan: string;               // PlanInfo.id
  requestedAt: Date;
  effectiveDate: Date;          // 有効開始日
  status: 'pending' | 'approved' | 'rejected';
  proratedAmount?: number;      // 差額料金
  reason?: string;
}

// API レスポンス型
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface BillingApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    details?: any;
  };
  pagination?: {
    page: number;
    limit: number;
    total: number;
    hasMore: boolean;
  };
}

// デフォルト値・設定
export const DEFAULT_PLAN_FEATURES: Record<PlanInfo['id'], PlanFeature[]> = {
  light: [
    {
      id: 'site_controller',
      name: '基本サイトコントローラー',
      description: '複数予約サイトの一元管理',
      category: 'core',
      included: true,
    },
    {
      id: 'customers',
      name: '顧客管理',
      description: '顧客情報の登録・管理',
      category: 'core',
      included: true,
    },
    {
      id: 'basic_ai',
      name: '基本AI分析',
      description: '基本的な顧客データ分析',
      category: 'core',
      included: true,
    },
  ],

  standard: [
    // Basic features +
    {
      id: 'site_controller',
      name: '全機能サイトコントローラー',
      description: '全予約サイトの完全統合',
      category: 'core',
      included: true,
    },
    {
      id: 'customers',
      name: '顧客管理',
      description: '顧客情報の登録・管理（拡張）',
      category: 'core',
      included: true,
    },
    {
      id: 'advanced_ai',
      name: '高度AI分析',
      description: '顧客動向・売上予測・離脱防止',
      category: 'advanced',
      included: true,
    },
    {
      id: 'marketing_automation',
      name: 'マーケティング自動化',
      description: 'AI生成メッセージ・キャンペーン管理',
      category: 'advanced',
      included: true,
    },
    {
      id: 'advanced_reports',
      name: '高度なレポート機能',
      description: 'カスタムレポート作成・分析',
      category: 'advanced',
      included: true,
    },
  ],

  premium: [
    // Standard features + Enterprise features
    {
      id: 'all_features',
      name: '全機能利用可能',
      description: 'Standard全機能＋カスタム機能',
      category: 'core',
      included: true,
    },
    {
      id: 'white_label',
      name: 'ホワイトラベル対応',
      description: '独自ブランディング・カスタマイズ',
      category: 'premium',
      included: true,
    },
    {
      id: 'custom_ai',
      name: 'カスタムAI機能',
      description: '専用AI分析・予測モデル',
      category: 'premium',
      included: true,
    },
    {
      id: 'dedicated_support',
      name: '専任サポート',
      description: '24時間サポート・専任担当者',
      category: 'premium',
      included: true,
    },
    {
      id: 'api_access',
      name: 'API連携',
      description: '外部システムとの完全統合',
      category: 'premium',
      included: true,
    },
  ],
};

// 3プラン体系のプラン制限（Light/Standard/Premium）
export const DEFAULT_PLAN_LIMITS: Record<PlanInfo['id'], PlanLimits> = {
  light: {
    staffCount: 5,
    reservationsPerMonth: 500,       // 小規模サロン向け
    customersLimit: 1000,
    storageGB: 10,
    aiAnalysisCount: 0,              // AI分析なし
    supportLevel: 'email',
    // multiStore: false,  // 削除: 各店舗が個別契約
    customBranding: false,
    apiAccess: false,
  },

  standard: {
    staffCount: 20,
    reservationsPerMonth: 2000,      // 中規模サロン向け
    customersLimit: 5000,
    storageGB: 50,
    aiAnalysisCount: 100,            // AI分析月100回
    supportLevel: 'chat',
    // multiStore: false,  // 削除: 各店舗が個別契約
    customBranding: false,
    apiAccess: false,
  },

  premium: {
    staffCount: 999999,              // 実質無制限
    reservationsPerMonth: 999999,    // 実質無制限
    customersLimit: 999999,          // 実質無制限
    storageGB: 999999,               // 実質無制限
    aiAnalysisCount: 999999,         // 実質無制限
    supportLevel: 'phone',
    // multiStore: true,  // 削除: 各店舗が個別契約
    customBranding: true,
    apiAccess: true,
  },
};